import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import imagesService from './imagesService'
import { STATUS } from 'features/enums' 
import { setConfirmModalData } from 'features/modal/modalSlice'

const errorMessage = (error) => {
    return (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
  }

const initialState = {
    images: [],
    imagesToUpload: [],
    uploadImagesStatus: STATUS.NOT_STARTED,
    imageDeleteStatus: STATUS.NOT_STARTED,
    getImagesStatus: STATUS.NOT_STARTED,
    viewImageUploadModal: false,
    viewGallery: false,
    slideShow: [],
    currentSlideIndex: 0,
    limit: 20,
    nextCursor: null,
    hasMoreImages: false,
    confirmDeleteImage: false,
} 

export const uploadImages = createAsyncThunk(
    'image/upload',
    async (data, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await imagesService.uploadImages(data, token)
        } catch (error) {
            const message = errorMessage(error)
            return thunkAPI.rejectWithValue(message)
        }
    }
)

export const deleteImage = createAsyncThunk(
    'image/delete',
    async (data, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await imagesService.deleteImage(data, token)
        } catch (error) {
            const message = errorMessage(error)
            return thunkAPI.rejectWithValue(message)
        }
    }
)

export const getImages = createAsyncThunk(
    'image/getImages',
    async (data, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await imagesService.getImages(data, token)
        } catch (error) {
            const message = errorMessage(error)
            return thunkAPI.rejectWithValue(message)
        }
    }
)

export  const imagesSlice = createSlice({
    name: 'images',
    initialState,
    reducers: { 
        setViewImageUploadModal: (state, action) => {
            
            if(action.payload === false){
                state.imagesToUpload = []
            }
            
            state.viewImageUploadModal = action.payload 

        },
        setImagesToUpload: (state, action) => {
            state.imagesToUpload = action.payload 
        },
        setViewGallery: (state, action) => {

            if(action.payload === false){
                state.currentSlideIndex = 0
            }else if(action.payload === true){
                state.slideShow = state.images
            }

            state.viewGallery = action.payload 
        },
        setSlideShowIndex: (state, action) => {
            state.currentSlideIndex = action.payload 
        },
        setConfirmDeleteImage: (state, action) => {
            state.confirmDeleteImage = action.payload
        }
    },
    extraReducers: (builder) => {

        builder
        .addCase(uploadImages.pending, (state) => {
            state.uploadImagesStatus = STATUS.LOADING
        })
        .addCase(uploadImages.fulfilled, (state, action) => {
            state.uploadImagesStatus = STATUS.SUCCESS
            state.imagesToUpload = []
            state.images.unshift(...action.payload.images)
            state.viewImageUploadModal = false
        })
        .addCase(uploadImages.rejected, (state, action) => {
            state.uploadImagesStatus = STATUS.ERROR
        })
        // -------------------------------------------------------------
        // get images ==================================================
        // --------------------------------------------------------------
        .addCase(getImages.pending, (state) => {
            state.getImagesStatus = STATUS.LOADING
        })
        .addCase(getImages.fulfilled, (state, action) => {
            state.getImagesStatus = STATUS.SUCCESS
            // No cursor = first/fresh load → replace. Cursor present → append.
            if (!action.meta.arg?.cursor) {
                state.images = action.payload.images ?? []
            } else {
                state.images = [...state.images, ...(action.payload.images ?? [])]
            }
            state.hasMoreImages = action.payload.hasMore ?? false
            state.nextCursor = action.payload.nextCursor ?? null
        })
        .addCase(getImages.rejected, (state, action) => {
            state.getImagesStatus = STATUS.ERROR
        })
        // -------------------------------------------------------------
        // delete image ==================================================
        // --------------------------------------------------------------
        .addCase(deleteImage.pending, (state) => { 
            state.imageDeleteStatus = STATUS.LOADING
        })
        .addCase(deleteImage.fulfilled, (state, action) => {
            state.imageDeleteStatus = STATUS.SUCCESS 
            const deletedImageId = action.payload.deletedImageId
            state.slideShow = state.slideShow.filter(image => image._id !== deletedImageId)
            state.currentSlideIndex = state.currentSlideIndex - 1 >= 0 ? state.currentSlideIndex - 1 : 0
            state.confirmDeleteImage = false
            state.images = state.images.filter(image => image._id !== deletedImageId)
            
        })
        .addCase(deleteImage.rejected, (state, action) => {
            state.imageDeleteStatus = STATUS.ERROR
            state.confirmDeleteImage = false
        })
    },

})

export const { setConfirmDeleteImage, setImagesToUpload, setViewImageUploadModal, setViewGallery, setSlideShowIndex } = imagesSlice.actions

export default imagesSlice.reducer
